import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DraggableWindow from './DraggableWindow';
import { lojasService, protocoloOnlineService } from '../../services/api';
import {
  gerarImagemProtocoloDataUrl,
  imprimirProtocoloDataUrl,
} from '../../utils/protocoloImage';

const PERIODOS = [
  { value: 1, label: 'Hoje' },
  { value: 3, label: 'Últimos 3 dias' },
  { value: 7, label: 'Últimos 7 dias' },
  { value: 15, label: 'Últimos 15 dias' },
  { value: 30, label: 'Últimos 30 dias' },
];

const STATUS_PROTOCOLO = {
  1: 'Aberto',
  2: 'Em trânsito',
  3: 'Recebido',
};

const formatarData = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const WindowPedidosEmitidos = ({
  onClose,
  onMinimize,
  isMinimized,
  zIndex,
  onFocus,
  userData,
  protocolService = protocoloOnlineService,
  storesService = lojasService,
}) => {
  const [pedidos, setPedidos] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [lojaOrigem, setLojaOrigem] = useState(String(
    userData?.loja_numero || userData?.loja_id || userData?.cdfil || ''
  ));
  const [dias, setDias] = useState(1);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState('');
  const [selecionado, setSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [acaoProtocolo, setAcaoProtocolo] = useState('');
  const [erroProtocolo, setErroProtocolo] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [gerandoImagem, setGerandoImagem] = useState(false);

  useEffect(() => {
    let ativo = true;
    storesService.getLojas()
      .then((data) => {
        if (ativo) setLojas(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, [storesService]);

  const carregarPedidos = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      if (!lojaOrigem) {
        setPedidos([]);
        return;
      }
      const response = await protocolService.listarPedidosEmitidos({
        dias,
        limite: 100,
        origemCdfil: lojaOrigem,
      });
      setPedidos(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar pedidos emitidos:', error);
      setPedidos([]);
      setErro(error?.message || 'Não foi possível carregar os pedidos emitidos.');
    } finally {
      setLoading(false);
    }
  }, [dias, lojaOrigem, protocolService]);

  useEffect(() => {
    carregarPedidos();
  }, [carregarPedidos]);

  const nomeLoja = useCallback((cdfil) => {
    const loja = lojas.find((item) =>
      String(item.numero) === String(cdfil) || String(item.id) === String(cdfil)
    );
    return loja?.nome || '';
  }, [lojas]);

  const pedidosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return pedidos;
    return pedidos.filter((pedido) => [
      pedido.pedido,
      pedido.chave_nfe,
      pedido.origem_cdfil,
      pedido.destino_cdfil,
      nomeLoja(pedido.destino_cdfil),
      pedido.protocolo_id,
    ].some((value) => String(value || '').toLowerCase().includes(termo)));
  }, [busca, nomeLoja, pedidos]);
  const lojasOrdenadas = useMemo(
    () => [...lojas].sort((a, b) => Number(a.numero) - Number(b.numero)),
    [lojas]
  );

  const pendentes = useMemo(
    () => pedidos.filter((pedido) => !pedido.protocolo_id).length,
    [pedidos]
  );

  const copiar = async (texto, identificador) => {
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(String(texto));
      setCopiado(identificador);
      window.setTimeout(() => setCopiado(''), 1800);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const fecharModal = () => {
    if (acaoProtocolo || gerandoImagem) return;
    setSelecionado(null);
    setConfirmacao(null);
    setResultado(null);
    setErroProtocolo('');
    setPreviewUrl('');
  };

  const iniciarProtocolo = async (pedido) => {
    if (!pedido?.chave_nfe || pedido.protocolo_id) return;
    setSelecionado(pedido);
    setConfirmacao(null);
    setResultado(null);
    setErroProtocolo('');
    setPreviewUrl('');
    setAcaoProtocolo('preview');
    try {
      const response = await protocolService.preview({
        chaveNfe: pedido.chave_nfe,
        origemCdfil: pedido.origem_cdfil,
      });
      setConfirmacao(response?.data || response);
    } catch (error) {
      console.error('Erro ao conferir protocolo:', error);
      setErroProtocolo(error?.message || 'Não foi possível conferir o destino do protocolo.');
    } finally {
      setAcaoProtocolo('');
    }
  };

  const gerarEtiqueta = async (dados) => {
    if (!dados?.protocolo) return;
    setGerandoImagem(true);
    setErroProtocolo('');
    try {
      const url = await gerarImagemProtocoloDataUrl({
        ...dados,
        nome_funcionario: userData?.nome || userData?.NOME || '',
        lojaNomeResolver: nomeLoja,
      });
      setPreviewUrl(url);
    } catch (error) {
      console.error('Erro ao gerar etiqueta do protocolo:', error);
      setPreviewUrl('');
      setErroProtocolo('O protocolo foi criado, mas não foi possível gerar a etiqueta para impressão.');
    } finally {
      setGerandoImagem(false);
    }
  };

  const finalizarProtocolo = async () => {
    if (!selecionado || !confirmacao) return;
    setErroProtocolo('');
    setAcaoProtocolo('finalizar');
    try {
      const response = await protocolService.create({
        chaveNfe: selecionado.chave_nfe,
        origemCdfil: selecionado.origem_cdfil,
        confirmacaoDestino: {
          destinatario_cdfun: confirmacao.destinatario_cdfun,
          destinatario_cdfil: confirmacao.destinatario_cdfil,
        },
      });
      const dados = response?.data || response;
      const protocoloId = dados?.protocolo || response?.protocolo || dados?.id;
      const protocoloCriado = {
        ...dados,
        protocolo: protocoloId,
        status: dados?.status ?? 1,
        data_cadastro: dados?.data_cadastro || new Date().toISOString(),
      };
      setResultado(protocoloCriado);
      setConfirmacao(null);
      setPedidos((atuais) => atuais.map((item) =>
        item.chave_nfe === selecionado.chave_nfe
          ? {
              ...item,
              protocolo_id: Number(protocoloId),
              protocolo_status: Number(protocoloCriado.status),
              protocolo_data: protocoloCriado.data_cadastro || new Date().toISOString(),
            }
          : item
      ));
      await gerarEtiqueta(protocoloCriado);
    } catch (error) {
      console.error('Erro ao criar protocolo:', error);
      setErroProtocolo(error?.message || 'Não foi possível criar o protocolo.');
      if (String(error?.message || '').includes('mudou após a confirmação')) {
        setConfirmacao(null);
      }
    } finally {
      setAcaoProtocolo('');
    }
  };

  const origemModal = confirmacao?.remetente_cdfil || selecionado?.origem_cdfil;
  const destinoModal = confirmacao?.destinatario_cdfil || selecionado?.destino_cdfil;

  return (
    <DraggableWindow
      title="Últimos Pedidos Emitidos"
      icon="fa-file-export"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={1060}
      initialTop={55}
      initialLeft={150}
      maxHeight="90vh"
      className="pedidos-emitidos-window"
    >
      <div className="pedidos-emitidos-wrapper">
        <div className="pedidos-emitidos-toolbar">
          <label>
            <span>Loja de origem</span>
            <select
              value={lojaOrigem}
              onChange={(event) => {
                setLojaOrigem(event.target.value);
                setErro('');
              }}
            >
              <option value="">Selecione a loja</option>
              {lojasOrdenadas.map((loja) => (
                <option key={loja.id || loja.numero} value={loja.numero}>
                  {loja.numero} - {loja.nome}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Período</span>
            <select value={dias} onChange={(event) => setDias(Number(event.target.value))}>
              {PERIODOS.map((periodo) => (
                <option key={periodo.value} value={periodo.value}>{periodo.label}</option>
              ))}
            </select>
          </label>
          <label className="pedidos-emitidos-search">
            <span>Localizar</span>
            <div>
              <i className="fas fa-magnifying-glass"></i>
              <input
                type="search"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Pedido, chave, destino ou protocolo"
                aria-label="Localizar pedido emitido"
                autoComplete="off"
                spellCheck="false"
              />
            </div>
          </label>
          <button type="button" className="consulta-btn-submit" onClick={carregarPedidos} disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'}`}></i>
            {loading ? ' Atualizando...' : ' Atualizar'}
          </button>
        </div>

        {!loading && !erro && pedidos.length > 0 && (
          <div className="pedidos-emitidos-summary">
            <span><strong>{pedidos.length}</strong> pedido(s) encontrado(s)</span>
            <span className={pendentes ? 'is-pending' : 'is-complete'}>
              <i className={`fas ${pendentes ? 'fa-circle-exclamation' : 'fa-circle-check'}`}></i>
              {pendentes ? `${pendentes} sem protocolo` : 'Todos possuem protocolo'}
            </span>
          </div>
        )}

        {erro && (
          <div className="consulta-error" role="alert">
            <strong><i className="fas fa-exclamation-triangle"></i> {erro}</strong>
          </div>
        )}

        {loading && pedidos.length === 0 && (
          <div className="consulta-loading pedidos-emitidos-empty">
            <i className="fas fa-spinner fa-spin"></i> Carregando pedidos emitidos...
          </div>
        )}

        {!loading && !erro && pedidosFiltrados.length === 0 && (
          <div className="pedidos-emitidos-empty">
            <i className="fas fa-inbox"></i>
            <strong>Nenhum pedido encontrado</strong>
            <span>Altere o período ou o texto da busca.</span>
          </div>
        )}

        {pedidosFiltrados.length > 0 && (
          <div className="pedidos-emitidos-table-wrap">
            <table className="pedidos-emitidos-table">
              <thead>
                <tr>
                  <th>Emissão</th>
                  <th>Pedido</th>
                  <th>Origem</th>
                  <th>Destino</th>
                  <th>Chave NF-e</th>
                  <th>Protocolo</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {pedidosFiltrados.map((pedido) => {
                  const chaveId = `chave-${pedido.chave_nfe}`;
                  const protocoloId = `protocolo-${pedido.protocolo_id}`;
                  return (
                    <tr key={`${pedido.pedido}-${pedido.chave_nfe}`}>
                      <td data-label="Emissão">{formatarData(pedido.data_emissao)}</td>
                      <td data-label="Pedido">
                        <strong className="pedidos-emitidos-pedido">
                          <i className="fas fa-file-invoice"></i>{pedido.pedido}
                        </strong>
                      </td>
                      <td data-label="Origem">
                        <span className="pedidos-emitidos-loja origin">
                          <i className="fas fa-store"></i>
                          <span><strong>{pedido.origem_cdfil}</strong>{nomeLoja(pedido.origem_cdfil) || 'Loja origem'}</span>
                        </span>
                      </td>
                      <td data-label="Destino">
                        <span className="pedidos-emitidos-loja destination">
                          <i className="fas fa-location-dot"></i>
                          <span><strong>{pedido.destino_cdfil}</strong>{nomeLoja(pedido.destino_cdfil) || 'Loja destino'}</span>
                        </span>
                      </td>
                      <td data-label="Chave NF-e">
                        <div className="pedidos-emitidos-chave">
                          <code title={pedido.chave_nfe}>{pedido.chave_nfe}</code>
                          <button
                            type="button"
                            onClick={() => copiar(pedido.chave_nfe, chaveId)}
                            title="Copiar chave NF-e"
                          >
                            <i className={`fas ${copiado === chaveId ? 'fa-check' : 'fa-copy'}`}></i>
                          </button>
                        </div>
                      </td>
                      <td data-label="Protocolo">
                        {pedido.protocolo_id ? (
                          <button
                            type="button"
                            className="pedidos-emitidos-protocolo"
                            onClick={() => copiar(pedido.protocolo_id, protocoloId)}
                            title="Copiar número do protocolo"
                          >
                            <i className={`fas ${copiado === protocoloId ? 'fa-check' : 'fa-circle-check'}`}></i>
                            <span>
                              <strong>#{pedido.protocolo_id}</strong>
                              {STATUS_PROTOCOLO[pedido.protocolo_status] || 'Criado'}
                            </span>
                          </button>
                        ) : (
                          <span className="pedidos-emitidos-sem-protocolo">
                            <i className="fas fa-clock"></i> Pendente
                          </span>
                        )}
                      </td>
                      <td data-label="Ação">
                        <button
                          type="button"
                          className="ios-btn-sm solid-blue pedidos-emitidos-create"
                          disabled={Boolean(pedido.protocolo_id)}
                          onClick={() => iniciarProtocolo(pedido)}
                        >
                          <i className={`fas ${pedido.protocolo_id ? 'fa-check' : 'fa-square-plus'}`}></i>
                          {pedido.protocolo_id ? ' Criado' : ' Gerar protocolo'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selecionado && (
        <div className="pedidos-protocolo-overlay" onMouseDown={fecharModal}>
          <div className="pedidos-protocolo-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="pedidos-protocolo-modal-header">
              <div>
                <i className="fas fa-route"></i>
                <span>
                  <small>Pedido {selecionado.pedido}</small>
                  <strong>{resultado ? 'Protocolo criado' : 'Confirmar novo protocolo'}</strong>
                </span>
              </div>
              <button
                type="button"
                onClick={fecharModal}
                disabled={Boolean(acaoProtocolo) || gerandoImagem}
                aria-label="Fechar"
              >
                <i className="fas fa-xmark"></i>
              </button>
            </div>

            <div className="pedidos-protocolo-modal-body">
              {acaoProtocolo === 'preview' && (
                <div className="pedidos-protocolo-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <strong>Conferindo loja e gerente...</strong>
                  <span>Nenhum protocolo está sendo criado nesta etapa.</span>
                </div>
              )}

              {erroProtocolo && (
                <div className="cria-protocolo-feedback cria-protocolo-feedback--error" role="alert">
                  <i className="fas fa-circle-exclamation"></i><span>{erroProtocolo}</span>
                </div>
              )}

              {confirmacao && (
                <>
                  <div className="cria-protocolo-result-grid">
                    <div>
                      <small>Loja origem</small>
                      <strong>{origemModal}{nomeLoja(origemModal) ? ` — ${nomeLoja(origemModal)}` : ''}</strong>
                    </div>
                    <div>
                      <small>Loja destino</small>
                      <strong>{destinoModal}{nomeLoja(destinoModal) ? ` — ${nomeLoja(destinoModal)}` : ''}</strong>
                    </div>
                    <div>
                      <small>Gerente destinatário</small>
                      <strong>{confirmacao.destinatario_nome || 'Gerente'} — Matrícula {confirmacao.destinatario_cdfun}</strong>
                    </div>
                    <div>
                      <small>Pedido</small>
                      <strong>{selecionado.pedido}</strong>
                    </div>
                    <div className="cria-protocolo-result-wide">
                      <small>Chave NF-e</small>
                      <strong className="cria-protocolo-mono">{selecionado.chave_nfe}</strong>
                    </div>
                  </div>
                  <div className="cria-protocolo-not-created">
                    <i className="fas fa-shield-halved"></i>
                    <span><strong>Nenhum protocolo foi criado ainda.</strong> Confira os dados e finalize.</span>
                  </div>
                </>
              )}

              {resultado && (
                <>
                  <div className="pedidos-protocolo-success">
                    <i className="fas fa-circle-check"></i>
                    <span><small>Protocolo criado com sucesso</small><strong>#{resultado.protocolo}</strong></span>
                  </div>
                  {previewUrl && (
                    <div className="pedidos-protocolo-preview">
                      <img src={previewUrl} alt={`Etiqueta do protocolo ${resultado.protocolo}`} />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="pedidos-protocolo-modal-actions">
              {!resultado && (
                <button type="button" className="ios-btn-sm" onClick={fecharModal} disabled={Boolean(acaoProtocolo)}>
                  Cancelar
                </button>
              )}
              {erroProtocolo && !confirmacao && !resultado && !acaoProtocolo && (
                <button type="button" className="ios-btn-sm tint-blue" onClick={() => iniciarProtocolo(selecionado)}>
                  <i className="fas fa-rotate"></i> Tentar novamente
                </button>
              )}
              {confirmacao && !resultado && (
                <button
                  type="button"
                  className="ios-btn-sm solid-blue"
                  onClick={finalizarProtocolo}
                  disabled={Boolean(acaoProtocolo)}
                >
                  <i className={`fas ${acaoProtocolo === 'finalizar' ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                  {acaoProtocolo === 'finalizar' ? ' Criando...' : ' Finalizar e criar'}
                </button>
              )}
              {resultado && (
                <>
                  {!previewUrl && !gerandoImagem && (
                    <button
                      type="button"
                      className="ios-btn-sm tint-blue"
                      onClick={() => gerarEtiqueta(resultado)}
                    >
                      <i className="fas fa-image"></i> Gerar etiqueta
                    </button>
                  )}
                  {(gerandoImagem || previewUrl) && (
                    <button
                      type="button"
                      className="ios-btn-sm solid-blue"
                      onClick={() => imprimirProtocoloDataUrl(previewUrl)}
                      disabled={!previewUrl}
                    >
                      <i className={`fas ${gerandoImagem ? 'fa-spinner fa-spin' : 'fa-print'}`}></i> Imprimir (80 mm)
                    </button>
                  )}
                  <button type="button" className="ios-btn-sm" onClick={fecharModal} disabled={gerandoImagem}>
                    <i className="fas fa-check"></i> Concluir
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </DraggableWindow>
  );
};

export default WindowPedidosEmitidos;
